import {RIGHTS} from '@/views/roles/rights';
import {namesOf} from '@/utils/names';
import {describeApiOutcome, responseOf} from '@/utils/apiOutcome';
import {cloudFunctionError, cloudFunctionErrorMessage, isFirebaseAuthRefusal} from '@/utils/cloudFunction';

const NEVER_DISCLOSED_PASSWORD_LENGTH = 32;

const NEVER_DISCLOSED_PASSWORD_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';

const EMAIL_ALREADY_EXISTS = 'auth/email-already-exists';

export const ONBOARDING_FAILED_FALLBACK = 'De klantgebruiker kon niet worden uitgenodigd.';

export const EMAIL_AVAILABILITY_UNKNOWN = 'Er kon niet worden gecontroleerd of dit e-mailadres al bekend is.';

export const PORTAL_ACCOUNT_FAILED_FALLBACK = 'Het portaalaccount kon niet worden aangemaakt.';

export const PORTAL_ACCOUNT_UNCERTAIN = 'Het portaal gaf geen antwoord, dus het is onbekend of het portaalaccount is aangemaakt. Controleer het bij Gebruikers voordat je het opnieuw probeert.';

export const EMAIL_TAKEN_IN_PORTAL = 'Er bestaat al een account met dit e-mailadres.';

export function splitContactName(contact) {
    const parts = String(contact || '').trim().split(/\s+/).filter(Boolean);

    if (!parts.length) {
        return {firstName: '', lastName: ''};
    }

    return {
        firstName: parts[0],
        lastName: parts.slice(1).join(' '),
    };
}

export function emptyInvitationForm() {
    return {firstName: '', lastName: '', email: '', phone: ''};
}

export function prefillFromCustomer(customer) {
    const {firstName, lastName} = splitContactName(customer && customer.contact);

    return {
        firstName,
        lastName,
        email: (customer && customer.email) || '',
        phone: (customer && customer.telephone) || '',
    };
}

export function findCustomerRoleId(roles, departmentName) {
    const customerRole = (roles || []).find(role => role
        && role.department === departmentName
        && namesOf(role.rights).includes(RIGHTS.IS_CUSTOMER));

    return customerRole ? customerRole.id : null;
}

function browserRandomValues(values) {
    return window.crypto.getRandomValues(values);
}

export function generateNeverDisclosedPassword(randomValues = browserRandomValues) {
    const bytes = randomValues(new Uint32Array(NEVER_DISCLOSED_PASSWORD_LENGTH));

    return Array.from(bytes)
        .map(byte => NEVER_DISCLOSED_PASSWORD_ALPHABET[byte % NEVER_DISCLOSED_PASSWORD_ALPHABET.length])
        .join('');
}

export function buildPortalUserPayload({form, roleId, departmentName, password}) {
    return {
        firstname: form.firstName,
        lastname: form.lastName,
        email: form.email,
        password,
        mobile: form.phone,
        roles: {[departmentName]: roleId},
        departments: [{name: departmentName}],
    };
}

export function buildClientUserPayload({form, firebaseId, customer}) {
    return {
        first_name: form.firstName,
        last_name: form.lastName,
        email: form.email,
        firebaseId,
        orgFirebaseId: customer.id,
        organization_name: customer.name,
    };
}

export function describeOnboardingOutcome(result) {
    return describeApiOutcome(result, {fallback: ONBOARDING_FAILED_FALLBACK, field: 'email'});
}

export function describeEmailAvailabilityOutcome(result) {
    return describeApiOutcome(result, {fallback: EMAIL_AVAILABILITY_UNKNOWN, field: 'email'});
}

export function portalUserId(result) {
    const response = responseOf(result);

    return (response && response.data && response.data.UserId) || null;
}

export function isEmailAlreadyInPortal(result) {
    return cloudFunctionError(result) === EMAIL_ALREADY_EXISTS;
}

export function describePortalAccountOutcome(result) {
    const firebaseId = portalUserId(result);

    if (firebaseId) {
        return {ok: true, firebaseId, emailError: null, message: null};
    }

    if (isEmailAlreadyInPortal(result)) {
        return {ok: false, firebaseId: null, emailError: EMAIL_TAKEN_IN_PORTAL, message: EMAIL_TAKEN_IN_PORTAL};
    }

    if (!isFirebaseAuthRefusal(result)) {
        return {ok: false, firebaseId: null, emailError: null, message: PORTAL_ACCOUNT_UNCERTAIN};
    }

    return {
        ok: false,
        firebaseId: null,
        emailError: null,
        message: cloudFunctionErrorMessage(result) || PORTAL_ACCOUNT_FAILED_FALLBACK,
    };
}
